import { LitElement } from "lit";
import { property } from "lit/decorators.js";

export type FormState = string | File | FormData | null | string;

export abstract class FormControlBase extends LitElement {
  /** Enables form-associated custom element behavior */
  static formAssociated = true;

  protected internals: ElementInternals;
  protected defaultValue = "";

  /** Native form attributes you’ll reuse everywhere */
  @property({ type: String, reflect: true }) name = "";
  @property({ type: String, reflect: true })
  formId: string | null = null;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: String }) requiredMessage = "This field is required.";

  constructor() {
    super();

    if (typeof (this as any).attachInternals === "function") {
      // @ts-ignore
      this.internals = this.attachInternals();
    } else {
      throw new Error(
        "ElementInternals / form-associated custom elements not supported in this browser."
      );
    }
  }

  // Native form APIs (pass-through)
  get form() {
    return this.internals.form;
  }
  get validity() {
    return this.internals.validity;
  }
  get validationMessage() {
    return this.internals.validationMessage;
  }
  get willValidate() {
    return this.internals.willValidate;
  }
  checkValidity() {
    return this.internals.checkValidity();
  }
  reportValidity() {
    return this.internals.reportValidity();
  }

  // Form callbacks
  formResetCallback() {
    this.onFormReset();
    this.syncFormValue();
    this.syncValidity();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
    this.onFormDisabled(disabled);
    this.syncValidity();
  }

  formStateRestoreCallback(state: FormState) {
    this.onFormStateRestore(state);
    this.syncFormValue();
    this.syncValidity();
  }

  // Hooks for subclasses

  /** Must return current “submission value” (string/file/formdata) */
  protected abstract getFormValue(): FormState;

  /** Must set the component state from restored state (usually string) */
  protected abstract setValueFromFormState(state: FormState): void;

  /** Must return the internal focusable control to anchor validity bubbles */
  protected abstract getValidityAnchor(): HTMLElement | undefined;

  /** Must return true if empty (for required) */
  protected abstract isEmpty(): boolean;

  /** Must return the native control you want to proxy to (input/textarea/select/...) */
  protected abstract getNativeControl(): HTMLElement | null;

  /** Optional: return first custom error message (or empty string) */
  protected getCustomErrorMessage(): string {
    return "";
  }

  protected validateForForm(): string {
    // return "" if OK, otherwise return error message
    return "";
  }

  /** Optional: reset component to default */
  protected onFormReset() {
    this.setValueFromFormState(this.defaultValue);
  }

  /** Optional: react to fieldset disabled */
  protected onFormDisabled(_disabled: boolean) { }

  /** Optional: handle BFCache restore */
  protected onFormStateRestore(state: FormState) {
    this.setValueFromFormState(state);
  }

  /**
   * Called by base after a proxy method mutates the native control.
   * Subclasses should pull native.value into their state and optionally validate.
   */
  protected onNativeValueMutated(_native: any): void { }

  // Shared utilities (form value and validity)

  protected syncFormValue() {
    if (!this.name || this.disabled) {
      this.internals.setFormValue(null);
      return;
    }

    const v = this.getFormValue();

    if (v === null) {
      this.internals.setFormValue(null);
      return;
    }

    this.internals.setFormValue(v);
  }

  protected syncValidity() {
    if (this.disabled) {
      this.internals.setValidity({});
      return;
    }

    // Required
    if (this.required && this.isEmpty()) {
      this.internals.setValidity(
        { valueMissing: true },
        this.requiredMessage || "This field is required.",
        this.getValidityAnchor()
      );
      return;
    }

    // Custom error (subclass)
    const msg = this.validateForForm();
    if (msg) {
      this.internals.setValidity(
        { customError: true },
        msg,
        this.getValidityAnchor()
      );
      return;
    }

    this.internals.setValidity({});
  }

  protected reemitNativeEvent(
    type: string,
    originalEvent: Event,
    init?: Partial<EventInit> & { detail?: any }
  ) {
    const cancelable = ["submit"].includes(type) ? true : false;
    const common: EventInit = { bubbles: true, composed: true, cancelable };

    const ev =
      init?.detail !== undefined
        ? new CustomEvent(type, { ...common, ...init })
        : new Event(type, { ...common, ...init });

    (ev as any).__originalEvent = originalEvent;

    this.dispatchEvent(ev);
  }

  /** Call once after initial render */
  protected captureDefaultValue(initial: string) {
    this.defaultValue = initial ?? "";
  }



  // Native proxy helpers (shared across all controls)

  protected get nativeControl(): any | null {
    return this.getNativeControl() as any;
  }

  protected get nativeInput(): HTMLInputElement | null {
    const el = this.getNativeControl();
    return el instanceof HTMLInputElement ? el : null;
  }

  protected get nativeTextarea(): HTMLTextAreaElement | null {
    const el = this.getNativeControl();
    return el instanceof HTMLTextAreaElement ? el : null;
  }

  /** Central sync after proxy calls */
  protected syncFromNative(runValidity = true) {
    const native = this.nativeControl;
    if (!native) return;

    this.onNativeValueMutated(native);
    this.syncFormValue();
    if (runValidity) this.syncValidity();
  }

  // focus/blur/click/showPicker
  focus(options?: FocusOptions) {
    this.nativeControl?.focus?.(options);
  }

  blur() {
    this.nativeControl?.blur?.();
  }

  click() {
    this.nativeControl?.click?.();
  }

  showPicker(): boolean {
    const input = this.nativeInput as any;
    if (!input || typeof input.showPicker !== "function") return false;

    try {
      input.showPicker();
      return true;
    } catch (err) {
      // Chromium throws NotAllowedError if not called from a user gesture
      // We intentionally swallow it because this is often used programmatically.
      return false;
    }
  }

  protected getSelectionTarget(): HTMLInputElement | HTMLTextAreaElement | null {
    const el = this.getNativeControl();
    if (!el) return null;

    // textarea always supports selection
    if (el instanceof HTMLTextAreaElement) return el;

    if (el instanceof HTMLInputElement) {
      const t = (el.type || "").toLowerCase();

      // types that don't support selection APIs
      if (
        t === "date" ||
        t === "time" ||
        t === "week" ||
        t === "month" ||
        t === "datetime-local" ||
        t === "number" ||
        t === "file"
      ) {
        return null;
      }

      return el;
    }

    return null;
  }

  get selectionStart(): number | null {
    return this.getSelectionTarget()?.selectionStart ?? null;
  }
  set selectionStart(v: number | null) {
    const t = this.getSelectionTarget();
    if (!t) return;
    t.selectionStart = v;
  }

  get selectionEnd(): number | null {
    return this.getSelectionTarget()?.selectionEnd ?? null;
  }
  set selectionEnd(v: number | null) {
    const t = this.getSelectionTarget();
    if (!t) return;
    t.selectionEnd = v;
  }

  get selectionDirection(): "forward" | "backward" | "none" | null {
    return this.getSelectionTarget()?.selectionDirection ?? null;
  }

  select() {
    this.getSelectionTarget()?.select?.();
  }

  setSelectionRange(start: number, end: number, direction?: "forward" | "backward" | "none") {
    this.getSelectionTarget()?.setSelectionRange?.(start, end, direction);
  }

  setRangeText(
    replacement: string,
    start?: number,
    end?: number,
    selectionMode?: "select" | "start" | "end" | "preserve"
  ) {
    const el = this.getSelectionTarget();
    if (!el || this.disabled || this.readonly) return;

    if (start !== undefined && end !== undefined) {
      el.setRangeText(replacement, start, end, selectionMode);
    } else {
      el.setRangeText(replacement);
    }

    this.syncFromNative(true);
  }

  // valueAsNumber/valueAsDate (input only)
  get valueAsNumber(): number {
    return this.nativeInput?.valueAsNumber ?? Number.NaN;
  }
  set valueAsNumber(v: number) {
    const input = this.nativeInput;
    if (!input || this.disabled || this.readonly) return;
    input.valueAsNumber = v;
    this.syncFromNative(true);
  }

  get valueAsDate(): Date | null {
    return this.nativeInput?.valueAsDate ?? null;
  }
  set valueAsDate(v: Date | null) {
    const input = this.nativeInput;
    if (!input || this.disabled || this.readonly) return;
    input.valueAsDate = v;
    this.syncFromNative(true);
  }

  // stepping (input only)
  stepUp(n?: number) {
    const input = this.nativeInput;
    if (!input || this.disabled || this.readonly) return;
    input.stepUp(n);
    this.syncFromNative(true);
  }

  stepDown(n?: number) {
    const input = this.nativeInput;
    if (!input || this.disabled || this.readonly) return;
    input.stepDown(n);
    this.syncFromNative(true);
  }

  // native-ish validity API
  setCustomValidity(message: string) {
    if (this.disabled) return;

    if (message) {
      this.internals.setValidity(
        { customError: true },
        message,
        this.getValidityAnchor()
      );
    } else {
      this.internals.setValidity({});
      this.syncValidity();
    }
  }
}
